<template>
  <div v-if="hiresLoading || conversationsLoading" class="tw-flex tw-justify-center tw-py-8">
    <WdsSpinner :size="24" indeterminate />
  </div>
  <WdsStack v-else direction-column gap-custom="2rem">
    <!-- Total Placements -->
    <WdsStack direction-row>
      <div class="tw-basis-1/2"><WdsText :text="$t('Total placements')" secondary /></div>
      <div class="tw-basis-1/2">
        <WdsText :text="totalPlacements.toString()" primary />
      </div>
    </WdsStack>

    <!-- Active Placements -->
    <WdsStack v-if="activePlacements.length > 0" direction-column gap-md>
      <WdsText :text="$t('Active placements')" secondary />
      <div
        v-for="hire in activePlacements"
        :key="hire.id"
        class="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
      >
        <WdsStack direction-row justify-space-between align-start>
          <WdsStack direction-column gap-xs class="tw-flex-1">
            <WdsText :text="hire.worker?.name ?? ''" primary medium />
            <WdsText :sub-text="hire.job?.name ?? ''" secondary />
            <WdsText v-if="hire.startDate" :sub-text="`${$t('Started')}: ${hire.startDate}`" secondary />
          </WdsStack>
          <WdsLabel success xs>{{ $t('Active') }}</WdsLabel>
        </WdsStack>
      </div>
    </WdsStack>

    <!-- Completed Placements -->
    <WdsStack v-if="completedPlacements.length > 0" direction-column gap-md>
      <WdsText :text="$t('Completed placements')" secondary />
      <div
        v-for="hire in completedPlacements"
        :key="hire.id"
        class="tw-border tw-border-gray-200 tw-rounded-lg tw-p-4"
      >
        <WdsStack direction-column gap-xs>
          <WdsText :text="hire.worker?.name ?? ''" primary medium />
          <WdsText :sub-text="hire.job?.name ?? ''" secondary />
          <WdsText
            v-if="hire.startDate || hire.endDate"
            :sub-text="`${hire.startDate ?? ''} - ${hire.endDate ?? ''}`"
            secondary
          />
        </WdsStack>
      </div>
    </WdsStack>

    <!-- Conversations -->
    <WdsStack v-if="conversationsList.length > 0" direction-column gap-md>
      <WdsText :text="$t('Active conversations')" secondary />
      <a
        v-for="conversation in conversationsList"
        :key="conversation.id"
        :href="conversation.url ?? '#'"
        class="-tw-ml-1"
      >
        <WdsButtonGhost class="tw-w-full tw-text-left">
          <div class="tw-flex tw-justify-between tw-items-center tw-w-full">
            <WdsStack gap-md align-center>
              <div class="tw-bg-true-gray-100 tw-p-3.5 tw-rounded">
                <WdsIcon :size="16" name="chat" class="tw-text-gray-600" />
              </div>
              <WdsText
                :text="conversation.subject"
                :sub-text="`${
                  conversation.latestMessage
                    ? `${$t('With')} ${conversation.latestMessage?.author?.name} ${$t('on')}`
                    : $t('On')
                } ${conversation.createdAt}`"
              />
            </WdsStack>
            <WdsIcon :size="16" name="chevron-right" class="tw-text-gray-600" />
          </div>
        </WdsButtonGhost>
      </a>
    </WdsStack>

    <WdsText
      v-if="totalPlacements === 0 && conversationsList.length === 0"
      class="tw-text-center"
      secondary
    >
      {{ $t('No activity found for this recruiter') }}
    </WdsText>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  WdsStack,
  WdsIcon,
  WdsText,
  WdsButtonGhost,
  WdsLabel,
  WdsSpinner,
} from '@worksome/wds-components'
import { useQuery } from '@vue/apollo-composable'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { useFragment } from '@/gql'
import { CONVERSATIONS_VIEW_FRAGMENT } from '@/components/WorkerDetailPanel/graphql/fragments'
import {
  RecruiterActivityHiresQuery,
  RecruiterConversationsQuery,
} from '@/components/RecruiterDetailPanel/graphql/queries'

const props = defineProps<{
  recruiterId: string
  companyRecruiterId: string
}>()

// Fetch hires attributed to this recruiter
const hiresVariables = computed(() => ({
  accounts: [currentAccount.globalId],
  recruiters: [props.recruiterId],
}))

const {
  loading: hiresLoading,
  result: hiresResult,
} = useQuery(RecruiterActivityHiresQuery, hiresVariables, {
  clientId: 'publicApi',
})

// Fetch conversations between company and recruiter
const conversationsVariables = computed(() => ({
  companyId: currentAccount.globalId,
  recruiterId: props.recruiterId,
}))

const {
  loading: conversationsLoading,
  result: conversationsResult,
} = useQuery(RecruiterConversationsQuery, conversationsVariables, {
  clientId: 'publicApi',
})

const hires = computed(() => hiresResult.value?.hires?.data ?? [])
const totalPlacements = computed(() => hiresResult.value?.hires?.paginatorInfo?.total ?? 0)

const activePlacements = computed(() =>
  hires.value.filter((hire) => hire.status === 'ACTIVE'),
)

const completedPlacements = computed(() =>
  hires.value.filter((hire) => hire.status === 'ENDED'),
)

const conversationsList = computed(() =>
  useFragment(CONVERSATIONS_VIEW_FRAGMENT, conversationsResult.value?.conversations?.data ?? []),
)
</script>
